import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { MaskedLines, FadeIn, Reveal } from "@/components/Motion";
import Lightbox from "@/components/Lightbox";
import QuartzRange from "@/components/QuartzRange";
import { STONES, STONE_DETAILS } from "@/data/stones";
import { KALINGA_RANGE, PARADIGM_RANGE } from "@/data/quartzRange";
import { useSeo } from "@/hooks/useSeo";

const RANGES = { kalinga: KALINGA_RANGE, paradigm: PARADIGM_RANGE };

function VarietyCard({ v, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <Reveal>
      <div className="group" data-testid={testid}>
        <div
          className="overflow-hidden aspect-[4/3] mb-4 cursor-zoom-in"
          onClick={() => setOpen(true)}
        >
          <img
            src={v.image}
            alt={v.name}
            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
          />
        </div>
        <p className="font-serif text-xl font-light tracking-tight">{v.name}</p>
        <p className="mt-1.5 text-sm font-light text-muted-foreground leading-relaxed">{v.note}</p>
      </div>
      <Lightbox image={open ? { src: v.image, name: v.name } : null} onClose={() => setOpen(false)} />
    </Reveal>
  );
}

export default function CollectionDetail() {
  const { slug } = useParams();
  const stone = STONES.find((s) => s.slug === slug);
  const detail = STONE_DETAILS[slug];

  useSeo({
    title: detail?.seoTitle,
    description: detail?.seoDesc,
    path: `/collections/${slug}`,
  });

  if (!stone || !detail) {
    return (
      <section className="max-w-3xl mx-auto px-6 pt-40 pb-40 text-center" data-testid="collection-not-found">
        <h1 className="font-serif text-4xl font-light tracking-tight mb-6">Collection not found.</h1>
        <Link to="/collections" className="text-xs tracking-[0.2em] uppercase border-b border-foreground/40 pb-1">
          View all collections
        </Link>
      </section>
    );
  }

  const idx = STONES.indexOf(stone);
  const prev = STONES[(idx - 1 + STONES.length) % STONES.length];
  const next = STONES[(idx + 1) % STONES.length];

  return (
    <div data-testid={`collection-page-${slug}`}>
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-32 md:pt-52 pb-20 md:pb-28">
        <FadeIn delay={0.1}>
          <Link
            to="/collections"
            data-testid="collection-back-link"
            className="group inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-secondary-foreground mb-10"
          >
            <ArrowLeft className="w-3.5 h-3.5 transition-transform duration-300 group-hover:-translate-x-1" />
            All collections
          </Link>
        </FadeIn>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-end">
          <div className="lg:col-span-5">
            <FadeIn delay={0.15}>
              <p className="text-xs tracking-[0.3em] uppercase text-secondary-foreground mb-6">
                Collection {stone.number} — {stone.origin}
              </p>
            </FadeIn>
            <h1
              data-testid="collection-title"
              className="font-serif font-light tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl"
            >
              <MaskedLines delay={0.2} lines={[stone.name]} />
            </h1>
            <FadeIn delay={0.7}>
              <p className="mt-8 text-base font-light leading-relaxed text-muted-foreground max-w-md">
                {detail.intro}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                {stone.finishes.map((f) => (
                  <span
                    key={f}
                    className="text-xs tracking-[0.15em] uppercase border border-border px-4 py-2 text-secondary-foreground"
                  >
                    {f}
                  </span>
                ))}
              </div>
              <Link
                to={`/contact?stone=${stone.slug}`}
                data-testid="collection-enquire-btn"
                className="group mt-10 inline-flex items-center gap-3 bg-primary text-primary-foreground text-xs tracking-[0.2em] uppercase px-8 py-4 transition-colors duration-300 hover:bg-foreground/80"
              >
                Enquire about {stone.name.toLowerCase()}
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </FadeIn>
          </div>
          <div className="lg:col-span-7">
            <FadeIn delay={0.4}>
              <div className="overflow-hidden aspect-[16/10]">
                <img src={stone.image} alt={stone.name} className="w-full h-full object-cover" />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {detail.brands ? (
        detail.brands.map((b) => (
          <section key={b.name} className="border-t border-border" data-testid={`brand-section-${b.name.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
              <Reveal>
                <div className="flex flex-wrap items-center gap-6 mb-4">
                  <img src={b.logo} alt={b.name} className="h-9 md:h-11 w-auto object-contain" />
                </div>
                <p className="max-w-lg text-base font-light leading-relaxed text-muted-foreground mb-14">
                  {b.blurb}
                </p>
              </Reveal>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                {b.varieties.map((v) => (
                  <VarietyCard key={v.name} v={v} testid={`variety-${v.name.toLowerCase().replace(/\s+/g, "-")}`} />
                ))}
              </div>
              {b.rangeKey && <QuartzRange range={RANGES[b.rangeKey]} brandKey={b.rangeKey} />}
            </div>
          </section>
        ))
      ) : (
        <section className="border-t border-border">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
            <Reveal>
              <p className="text-xs tracking-[0.3em] uppercase text-secondary-foreground mb-4">Signature styles</p>
              <h2 className="font-serif text-3xl sm:text-4xl font-light tracking-tight mb-14">
                Stones we <span className="italic">love.</span>
              </h2>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
              {detail.varieties.map((v) => (
                <VarietyCard key={v.name} v={v} testid={`variety-${v.name.toLowerCase().replace(/\s+/g, "-")}`} />
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 flex items-center justify-between gap-6">
          <Link
            to={`/collections/${prev.slug}`}
            data-testid="collection-prev"
            className="group inline-flex items-center gap-3 text-xs tracking-[0.2em] uppercase text-secondary-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-1" />
            <span className="font-serif text-lg md:text-xl normal-case tracking-tight font-light">{prev.name}</span>
          </Link>
          <Link
            to={`/collections/${next.slug}`}
            data-testid="collection-next"
            className="group inline-flex items-center gap-3 text-xs tracking-[0.2em] uppercase text-secondary-foreground hover:text-foreground transition-colors text-right"
          >
            <span className="font-serif text-lg md:text-xl normal-case tracking-tight font-light">{next.name}</span>
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </div>
  );
}
